import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { logAction } from '../lib/actionLogger';

// ── Tree + detail queries ───────────────────────────────────
export function useProjectsTree() {
  return useQuery({
    queryKey: queryKeys.projects.tree,
    queryFn: () => api.get('/projects/tree'),
  });
}

export function useProjectFull(id) {
  return useQuery({
    queryKey: queryKeys.projects.full(id),
    queryFn: () => api.get(`/projects/${id}/full`),
    enabled: !!id,
  });
}

export function useProjectRollup(id, scope = 'subtree') {
  return useQuery({
    queryKey: queryKeys.projects.rollup(id, scope),
    queryFn: () => api.get(`/projects/${id}/rollup?scope=${scope}`),
    enabled: !!id,
  });
}

export function useProjectTimeEntries(id, scope = 'subtree') {
  return useQuery({
    queryKey: queryKeys.projects.timeEntries(id, scope),
    queryFn: () => api.get(`/projects/${id}/time-entries?scope=${scope}`),
    enabled: !!id,
  });
}

export function useProjectExpenses(id, scope = 'subtree') {
  return useQuery({
    queryKey: queryKeys.projects.expenses(id, scope),
    queryFn: () => api.get(`/projects/${id}/expenses?scope=${scope}`),
    enabled: !!id,
  });
}

// ── Staycation extras ─────────────────────────────────────────
export function useProjectProduction(id) {
  return useQuery({
    queryKey: ['projects', 'production', id],
    queryFn: () => api.get(`/projects/${id}/production`),
    enabled: !!id,
  });
}

export function useProjectBudgetRollup(id) {
  return useQuery({
    queryKey: ['projects', 'budget-rollup', id],
    queryFn: () => api.get(`/projects/${id}/budget-rollup`),
    enabled: !!id,
  });
}

export function useMyAssignments() {
  return useQuery({
    queryKey: ['projects', 'my-assignments'],
    queryFn: () => api.get('/projects/my-assignments'),
  });
}

export function useProjectMembers(projectId) {
  return useQuery({
    queryKey: ['project_members', projectId],
    queryFn: () => api.get(`/projects/${projectId}/members`),
    enabled: !!projectId,
  });
}

export function useCreateProjectMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project_id, ...body }) => api.post(`/projects/${project_id}/members`, body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['project_members', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      logAction({
        action: 'Add Project Member',
        description: `Added member to project ${vars.project_id} as ${vars.project_role} @ $${vars.hourly_rate}/hr`,
        details: {
          projectId: vars.project_id,
          profileId: vars.profile_id,
          projectRole: vars.project_role,
          hourlyRate: vars.hourly_rate,
        },
      });
    },
  });
}

export function useDeleteProjectMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project_id, profile_id }) =>
      api.del(`/projects/${project_id}/members/${profile_id}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['project_members', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      logAction({
        action: 'Remove Project Member',
        description: `Removed member ${vars.profile_id} from project ${vars.project_id}`,
        details: { projectId: vars.project_id, profileId: vars.profile_id },
      });
    },
  });
}

// ── Project CRUD ────────────────────────────────────────────
function invalidateAllProjects(qc) {
  qc.invalidateQueries({ queryKey: queryKeys.projects.all });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/projects', body),
    onSuccess: (data) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Create Project',
        description: `Created project ${data.name} (${data.kind})`,
        details: {
          id: data.id,
          name: data.name,
          kind: data.kind,
          status: data.status,
          parentId: data.parent_id,
          ownerId: data.owner_id,
        },
      });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }) => api.put(`/projects/${id}`, fields),
    onSuccess: (data) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Update Project',
        description: `Updated project ${data.name} (${data.status})`,
        details: {
          id: data.id,
          name: data.name,
          kind: data.kind,
          status: data.status,
        },
      });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/projects/${id}`),
    onSuccess: (_d, id) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Delete Project',
        description: `Deleted project ${id}`,
        details: { id },
      });
    },
  });
}

export function useMoveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parent_id }) => api.put(`/projects/${id}/move`, { parent_id }),
    onSuccess: (_d, vars) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Move Project',
        description: `Moved project ${vars.id} under parent ${vars.parent_id}`,
        details: { id: vars.id, parentId: vars.parent_id },
      });
    },
  });
}

// ── Task CRUD ───────────────────────────────────────────────
export function useCreateProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/project-tasks', body),
    onSuccess: (data) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Create Project Task',
        description: `Created task ${data.title}`,
        details: { id: data.id, title: data.title, projectId: data.project_id },
      });
    },
  });
}

export function useUpdateProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }) => api.put(`/project-tasks/${id}`, fields),
    onSuccess: (data) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Update Project Task',
        description: `Updated task ${data.title}${data.done ? ' — completed' : ''}`,
        details: { id: data.id, title: data.title, done: data.done },
      });
    },
  });
}

export function useDeleteProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/project-tasks/${id}`),
    onSuccess: (_d, id) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Delete Project Task',
        description: `Deleted task ${id}`,
        details: { id },
      });
    },
  });
}

// ── Note / File CRUD ────────────────────────────────────────
export function useCreateProjectNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/project-notes', body),
    onSuccess: (data) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Create Project Note',
        description: `Added note ${data.title} to project ${data.project_id}`,
        details: { id: data.id, title: data.title, projectId: data.project_id },
      });
    },
  });
}

export function useUpdateProjectNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }) => api.put(`/project-notes/${id}`, fields),
    onSuccess: (data) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Update Project Note',
        description: `Edited note ${data.title}`,
        details: { id: data.id, title: data.title },
      });
    },
  });
}

export function useDeleteProjectNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/project-notes/${id}`),
    onSuccess: (_d, id) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Delete Project Note',
        description: `Deleted note ${id}`,
        details: { id },
      });
    },
  });
}

export function useCreateProjectFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/project-files', body),
    onSuccess: (data) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Add Project File',
        description: `Added ${data.kind} link ${data.label} to project ${data.project_id}`,
        details: {
          id: data.id,
          label: data.label,
          url: data.url,
          kind: data.kind,
          projectId: data.project_id,
        },
      });
    },
  });
}

export function useDeleteProjectFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/project-files/${id}`),
    onSuccess: (_d, id) => {
      invalidateAllProjects(qc);
      logAction({
        action: 'Delete Project File',
        description: `Deleted project file ${id}`,
        details: { id },
      });
    },
  });
}

// ── Tree utilities ──────────────────────────────────────────
/** Build nested tree from a flat list with parent_id. Preserves order. */
export function buildTree(flat) {
  if (!Array.isArray(flat)) return [];
  const byId = new Map();
  flat.forEach((p) => byId.set(p.id, { ...p, children: [] }));
  const roots = [];
  byId.forEach((node) => {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

/** Flatten tree adding depth. */
export function flattenTree(tree, depth = 0, out = []) {
  if (!Array.isArray(tree)) return out;
  tree.forEach((node) => {
    out.push({ ...node, depth });
    if (node.children?.length) flattenTree(node.children, depth + 1, out);
  });
  return out;
}

/** Collect all descendant IDs of a node (plus the node itself). */
export function collectSubtreeIds(tree, targetId) {
  const out = new Set();
  const walk = (nodes) => {
    for (const n of nodes) {
      if (n.id === targetId) {
        const gather = (x) => {
          out.add(x.id);
          (x.children || []).forEach(gather);
        };
        gather(n);
        return true;
      }
      if (n.children?.length && walk(n.children)) return true;
    }
    return false;
  };
  walk(tree);
  return out;
}
